import { collectionGroup, query, where, getDocs, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface NoteWithCourseInfo {
    // User data
    noteId: string;
    userId: string;
    completionStatus: boolean;
    completedAt?: Timestamp;
    isFavorited?: boolean; // Added for favorites functionality
    notes: {
        content: string;
        created?: Timestamp;
        updatedAt: Timestamp;
    };

    // Parent document data
    courseId: string;
    courseName: string;
    courseDescription: string;
    youtubeLink?: string;
    soundCloudLink?: string;
    docsLink?: string;
    parentDocPath: string;
}

export const fetchAllUserNotes = async (userId: string): Promise<NoteWithCourseInfo[]> => {
    try {
        // Step 1: Collection group query for all user's userData documents
        // We need to ensure userData documents have a userId field for this to work
        const userDataQuery = query(
            collectionGroup(db, 'userData'),
            where('userId', '==', userId),
            where('notes', '!=', null)
        );

        const snapshot = await getDocs(userDataQuery);

        // Step 2: Extract parent document IDs and fetch course metadata
        const notesWithCourseInfo = await Promise.all(
            snapshot.docs.map(async (doc) => {
                const userData = doc.data();

                // Get the parent document (the course/session document)
                const parentRef = doc.ref.parent.parent;
                if (!parentRef) {
                    console.error('Parent reference not found for document:', doc.id);
                    return null;
                }

                const courseDoc = await getDoc(parentRef);
                const courseData = courseDoc.data();

                if (!courseData) {
                    console.error('Course data not found for:', parentRef.path);
                    return null;
                }

                return {
                    noteId: doc.id,
                    userId: userId,
                    completionStatus: userData.completionStatus || false,
                    completedAt: userData.completedAt,
                    isFavorited: userData.isFavorited || false,
                    notes: userData.notes,
                    courseId: courseDoc.id,
                    courseName: courseData.name || courseData.title || 'Unknown',
                    courseDescription: courseData.description || '',
                    youtubeLink: courseData.youtubeLink,
                    soundCloudLink: courseData.soundCloudLink,
                    docsLink: courseData.docsLink,
                    parentDocPath: parentRef.path
                };
            })
        );

        // Filter out any null results
        return notesWithCourseInfo.filter(note => note !== null) as NoteWithCourseInfo[];
    } catch (error) {
        console.error('Error fetching user notes:', error);
        throw error;
    }
}; 